import { useEffect, useState, useMemo } from "react";
import { Search as SearchIcon } from "lucide-react";
import { motion } from "framer-motion";
import { mediaApi } from "../api";
import MediaCard from "../components/MediaCard";

export default function MediaSearch() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [folder, setFolder] = useState(""); // filtro por pasta/colecção (ID pai)

  useEffect(() => {
    mediaApi.catalog().then(setItems).catch(() => {});
  }, []);

  // Descobre folders (parents) — apenas quando o tipo de conteúdo é
  // áudio (AU), porque é aí que a separação por "colecção" faz sentido
  // (Áudios IHTP vs Áudios Scott Schmaren). Para vídeos, cada workshop
  // é uma folder individual e não uma colecção — os chips não fazem
  // sentido nesse contexto.
  const folders = useMemo(() => {
    if (type !== "AU") return [];
    const parents = items.filter(
      (i) =>
        i.ID_ConteudoMediaCenterPai === null &&
        i.ID_TipoConteudoMediaCenter === "AU"
    );
    return parents
      .map((p) => ({
        id: p.ID_ConteudoMediaCenter,
        label: p.Descricao,
        type: p.ID_TipoConteudoMediaCenter,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, type]);

  // Reset folder chip quando o tipo muda (evita filtros incompatíveis).
  useEffect(() => {
    setFolder("");
  }, [type]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((i) => {
      if (!i.MediaUrl) return false; // pastas vazias
      if (type && i.ID_TipoConteudoMediaCenter !== type) return false;
      if (folder && String(i.ID_ConteudoMediaCenterPai) !== String(folder)) return false;
      if (!s) return true;
      return (i.Descricao || "").toLowerCase().includes(s);
    });
  }, [q, items, type, folder]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 md:px-8 lg:px-12 py-12 max-w-[1600px] mx-auto"
      data-testid="media-search-page"
    >
      <div className="max-w-2xl mx-auto mb-10">
        <p className="text-xs tracking-[0.3em] uppercase text-[#D1CFC9] font-bold mb-2 text-center">Catálogo</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light text-center mb-6">Todos os conteúdos IHTP</h1>
        <div className="relative">
          <SearchIcon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Pesquisar áudios, vídeos, formações..."
            className="w-full pl-12 text-lg"
            data-testid="media-search-input"
          />
        </div>
        <div className="flex gap-2 mt-4 justify-center flex-wrap">
          {[
            { v: "", l: "Todos" },
            { v: "AU", l: "Áudios" },
            { v: "V2", l: "Vídeos" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setType(f.v)}
              className={`px-4 py-1.5 rounded-full text-sm transition ${
                type === f.v ? "bg-[#D1CFC9] text-[#0F1829] font-semibold" : "bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
              data-testid={`media-search-filter-${f.v || "all"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
        {folders.length > 1 && (
          <div className="flex gap-2 mt-3 justify-center flex-wrap" data-testid="media-search-folders">
            <button
              onClick={() => setFolder("")}
              className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest transition ${
                folder === ""
                  ? "bg-[#978D78] text-[#0F1829] font-bold"
                  : "bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
              data-testid="media-search-folder-all"
            >
              Todas as colecções
            </button>
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => setFolder(String(f.id))}
                className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest transition ${
                  String(folder) === String(f.id)
                    ? "bg-[#978D78] text-[#0F1829] font-bold"
                    : "bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
                data-testid={`media-search-folder-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="text-sm text-slate-500 mb-4" data-testid="media-search-count">
        {results.length} resultado{results.length !== 1 ? "s" : ""}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6" data-testid="media-search-results">
        {results.slice(0, 100).map((i) => (
          <MediaCard key={i.ID_ConteudoMediaCenter} item={i} fluid />
        ))}
      </div>
    </motion.div>
  );
}
